<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">Аналитика</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="show-charts" class="custom-btn-secondary mr-1" @click="toggleChart">
          <font-awesome-icon icon="chart-bar"/>
        </b-button>
        <b-tooltip target="show-charts" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Графики
        </b-tooltip>
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <font-awesome-icon icon="cog"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :hide-search="true"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          :disabled-submit="isDateEmpty"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Период</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input id="input_startDate" size="sm" type="date" v-model.date="filters.startDate" :state="isDateEmpty ? false : null"/>
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input id="input_endDate" size="sm" type="date" v-model.date="filters.endDate" :state="isDateEmpty ? false : null"/>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" v-if="isDateEmpty" class="d-flex justify-content-center">
            <small class="text-danger">Укажите даты периода!</small>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Объекты</label>
            <location-multi-select
              id="location_multi_select"
              ref="location_multi_select"
              @onSelect="onSelectLocation"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Сотрудники</label>
            <user-multi-select
              v-if="canReadOther()"
              id="user_multi_select"
              ref="user_multi_select"
              @onSelect="onSelectUser"
            />
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="calculator-project"
            ref="calculator_project"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy="isBusy"
            :items="items"
            :fields="renderedFields"
            :foot-clone="true"
            no-footer-sorting
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="6"/>
            </template>

            <template #cell(index)="data">
              {{ data.index + 1 }}
            </template>

            <template #cell(payment)="row">{{ price(row.item.payment) }}</template>
            <template #cell(nds_price)="row">{{ price(row.item.nds_price) }}</template>
            <template #cell(payment_without_nds)="row">{{ price(row.item.payment_without_nds) }}</template>
            <template #cell(total_nds_price)="row">{{ price(row.item.total_nds_price) }}</template>
            <template #cell(total_price_without_nds)="row">{{ price(row.item.total_price_without_nds) }}</template>
            <template #cell(percent_profit)="row">{{ price(row.item.percent_profit) }}</template>
            <template #cell(management_expenses)="row">{{ price(row.item.management_expenses) }}</template>
            <template #cell(total_price_without_expenses)="row">{{ price(row.item.total_price_without_expenses) }}</template>
            <template #cell(total_workers_service_commission)="row">{{ price(row.item.total_workers_service_commission) }}</template>
            <template #cell(total_workers_with_commission)="row">{{ price(row.item.total_workers_with_commission) }}</template>
            <template #cell(hours_count)="row">{{ price(row.item.hours_count) }}</template>

            <template #cell(total_price)="row">{{ price(row.item.total_price) }}</template>
            <template #cell(total_workers)="row">{{ price(row.item.total_workers) }}</template>

            <template #cell(custom_total_delta)="row">{{ price(row.item.custom_total_delta) }}</template>
            <template #cell(total_manager)="row">{{ price(row.item.total_manager) }}</template>
            <template #cell(total_project)="row">{{ price(row.item.total_project) }}</template>
            <template #cell(profit_with_man_exp)="row">{{ price(row.item.profit_with_man_exp) }}</template>
            <template #cell(profit_without_man_exp)="row">{{ price(row.item.profit_without_man_exp) }}</template>

            <template #cell(marginality)="row">
              <span style="font-size: large">
                <b-badge :variant="getMarginalityVariant(row.item)">{{ price(row.item.marginality) }} %</b-badge>
              </span>
            </template>

            <template #cell(task_exit)="row">
              {{ price(row.item.task_exit) }} %
            </template>

            <template #cell(location_payment_type)="row">
                <font-awesome-icon
                  :set="pt = paymentTypeIcon(row.item.location_payment_type)"
                  :icon="['fas', pt.icon]"
                  :title="pt.title"
                  :style="paymentTypeStyle(row.item.location_payment_type)"/>
              </template>

            <template #cell(partners_price)="row">{{ price(row.item.partners_price) }}</template>
            <template #cell(recruitment_price)="row">{{ price(row.item.recruitment_price) }}</template>

            <template v-if="total != null" #foot()="row">
                {{ price(total[row.field.key]) }}
            </template>
          </b-table>

          <div v-if="showChart">
            <b-card v-if="isBusy" size="sm" class="text-center text-muted">
              <b-spinner class="align-middle" v-if="isBusy"/>
              <strong v-if="isBusy" class="ml-2">Загрузка графика...</strong>
            </b-card>
            <e-chart-builder
              v-else
              chart-id="analytics-chart"
              class="EChart"
              :items="items"
              :fields="fields"
              :per-page="perPage"
              @toggle-index="toggleIndexField"
            />
          </div>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            :page-options="pageOptions"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>Нет записей в аналитике.</h5>
      </div>
    </div>

    <dynamic-tables
      storage-key="analytics_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="location_id"
    />

  </div>
</template>

<script>
import analytics from "../../network/analytics";
  import dateFormatter from "../../utilites/dateFormatter";
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import EChartBuilder from "../../utilites/commonUi/EChartBuilder.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import pageSearchService from "../../utilites/pageSearchService";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
    import LocationMultiSelect from "../Locations/LocationMultiSelect";
    import UserMultiSelect from "../Users/UserMultiSelect";

export default {
  name: "Analytics",
  components: { TablesSkeleton, FiltersDrawer, DynamicTables, LocationMultiSelect, UserMultiSelect, EChartBuilder, PaginationFooter },
  mixins: [
    analytics,
    dateFormatter, helpers, pageSearchService, permissions
  ],
  data() {
    return {
      items: [],
      serviceStartField: [],
      fields: [
        {
          key: 'location_id',
          label: 'id',
          sortable: true,
          visible: true,
          type: 'category'
        },
        {
          key: 'location_name',
          label: 'Объект',
          sortable: true,
          visible: true,
          tdClass: 'text-left',
          type: 'category'
        },
        {
          key: 'location_payment_type',
          label: 'Тип оплаты',
          sortable: true,
          visible: true,
        },
        {
          key: 'payment',
          label: 'Ставка заявки с НДС',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'nds_price',
          label: 'НДС',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'payment_without_nds',
          label: 'Ставка без НДС',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'total_nds_price',
          label: 'Общий НДС',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'total_price_without_nds',
          label: 'Сумма без НДС',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'percent_profit',
          label: 'Налог на прибыль',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'management_expenses',
          label: 'Упр. расходы',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'total_price_without_expenses',
          label: 'Сумма без расходов',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'total_workers_service_commission',
          label: 'Сервис по выплатам',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'total_workers_with_commission',
          label: 'Выплата исполнителям с учетом комиссии',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'hours_count',
          label: 'Часы',
          sortable: true,
          visible: true,
          type: 'value',
          measure: 'часов'
        },
        {
          key: 'total_price',
          label: 'Прибыль (Итого с заказчика)',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'total_workers',
          label: 'Прибыль исполнителям (ЗП)',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'custom_total_delta',
          label: 'Всего до выплаты менеджеру и руководителю',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '%'
        },
        {
          key: 'total_manager',
          label: 'Процент менеджера',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'total_project',
          label: 'Процент руководителя',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'marginality',
          label: 'Маржа',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '%'
        },
        {
          key: 'workers_coefficient',
          label: 'Коеф. текучести',
          sortable: true,
          visible: true,
          type: 'value',
        },
        {
          key: 'profit_with_man_exp',
          label: 'Прибыль c упр. расходом',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'profit_without_man_exp',
          label: 'Прибыль без упр. Расходов',
          sortable: true,
          visible: true,
          type: 'value',
          measure: '₽'
        },
        {
          key: 'work_type_name',
          label: 'Вид работ',
          sortable: true,
          visible: true,
        },
        {
          key: 'task_exit',
          label: 'Заявка/Выход',
          sortable: false,
          visible: true,
          type: 'value',
          measure: '%'
        },
      ],
      costTypeFields: [],
      sortBy: 'id',
      sortDesc: false,
      isBusy: false,
      pageOptions: [10, 25, 50, 100, 500, { value: 10000, text: 'Все'}],
      filters: this.getDefaultFilters(),
      total: null,
      showChart: false,
    }
  },
  async created() {
    const query = this.$route.query;

    if (query) {
      if (query.startDate && query.endDate) {
        this.filters.startDate = query.startDate;
        this.filters.endDate = query.endDate;
      }

      if (query.locations) {
        this.filters.locations = query.locations.split(',');
      }

      if (query.users) {
        this.filters.users = query.users.split(',');
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.onSearch();
  },
  async mounted() {
    if (this.$refs.location_multi_select) {
      await this.$refs.location_multi_select.configure(this.filters.locations);
    }

    if (this.$refs.user_multi_select) {
      await this.$refs.user_multi_select.configure(this.filters.users);
    }
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        ...this.serviceStartField,
        ...this.visibleFields,
        ...this.costTypeFields
      ];
    },
    activeFilterTags() {
      const tags = [];
      const start = this.filters.startDate;
      const end = this.filters.endDate;

      if (start || end) {
        let dateTag = '';

          if (start && end) {
            dateTag = `${this.formatTableDate(start)} - ${this.formatTableDate(end)}`;
          } else if (start) {
            dateTag = `с ${this.formatTableDate(start)}`;
          } else {
            dateTag = `по ${this.formatTableDate(end)}`;
          }
          tags.push({ key: 'date', label: dateTag });
      }

      if (this.filters.locations && this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.users && this.filters.users.length > 0) {
        tags.push({ key: 'user', label: `Сотрудники: ${this.filters.users.length}`,});
      }

      return tags;
    },
    isDateEmpty() {
      return !this.filters.startDate || !this.filters.endDate;
    },
  },
  methods: {
    async getItems() {
      const locationIds = this.filters.locations.map(item => "&locations[]=" + item);
      const usersIds = this.filters.users.map(item => "&users[]=" + item);

      const data = {
        page: this.currentPage,
        size: this.perPage,
        start: this.filters.startDate ? this.getDateToServer(this.filters.startDate).toISOString() : null,
        end: this.filters.endDate ? this.getDateToServer(this.filters.endDate).toISOString() : null,
        locations: locationIds.join(''),
        users: usersIds.join(''),
      }

      this.isBusy = true;
      const result = await this.getCalculateProjects(data);
      this.isBusy = false;

      if (result.data == null) {
        this.items = [];
        this.totalRows = 0;
        this.total = null;
        return;
      }

      this.items = result.data.items;
      this.total = result.data.total;
      this.totalRows = result.data.total.count;
      this.configCostColumns(this.items, this.total);
      this.clearIndexField();
    },
    getMarginalityVariant(item) {
      const marginality = item.marginality;
      if (marginality <= 0) {
        return "dark";
      }

      if (marginality > 0 && marginality < 5) {
        return "danger";
      }

      if (marginality >= 5 && marginality < 9) {
        return "warning";
      }

      if (marginality >= 9) {
        return "success";
      }
    },
    // Filters
    getDefaultFilters() {
      let filters = {
        startDate: null,
        endDate: null,
        users: [],
        locations: [],
      };

      filters.startDate = this.getStartDateFilter();
      filters.endDate = this.getEndDateFilter();
      return filters;
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      if (this.$refs.location_multi_select) {
        this.$refs.location_multi_select.onClearSelected();
      }

      if (this.$refs.user_multi_select) {
        this.$refs.user_multi_select.onClearSelected();
      }

      this.filters = filters;
      this.onSearch();
    },
    getStartDateFilter() {
      let date = new Date();
      date.setDate(date.getDate() - 15);
      return this.formatDate(date);
    },
    getEndDateFilter() {
      return this.formatDate(new Date());
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(item => item.id);
    },
    onSelectUser(users) {
      this.filters.users = users.map(item => item.id);
    },
    removeFilterTag(tag) {
      if (tag.key === 'date') {
        this.filters.startDate = this.getStartDateFilter();
        this.filters.endDate = this.getEndDateFilter();
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'user') {
        this.filters.users = [];
        this.$refs.user_multi_select.onClearSelected();
      }

      this.onSearch();
    },
    // Тип оплаты
    paymentTypeIcon(flag) {
      switch (flag) {
        case 0:
          return {icon: "coins", title: "Нал", style: {color: 'darkgreen'}}
        case 1:
          return {icon: "file-invoice-dollar", title: "ООО", style: {color: 'darkgreen'}}
        case 2:
          return {icon: "user", title: "ИП", style: {color: 'darkgreen'}}
        case 3:
          return {icon: "file-invoice-dollar", title: "СМЗ", style: {color: 'darkgreen'}}
        default:
          return {icon: "user-slash", title: "Неизвестно", style: {color: 'darkgreen'}}
      }
    },
    paymentTypeStyle(flag) {
      switch (flag) {
        case 0:
          return {color: 'darkgreen'}
        case 1:
          return {color: 'darkblue'}
        case 2:
          return {color: 'darkred'}
        case 3:
          return {color: 'darkorange'}
        default:
          return {color: 'black'}
      }
    },
    price(value) {
      if (value == null) {
        return value;
      }
      if (!this.isCustomNumber(value)) {
        return value;
      }

      return this.priceFormat(value, false);
    },
    // Конфигурация колонок таблицы
    configCostColumns(items, total) {
      if (total.location_costs == null) {
        this.costTypeFields = [];
        return;
      }

      this.costTypeFields = [];
      total.location_costs.forEach(function (lc) {
        let key = `location_cost_${lc.cost_type}`;

        const column = {
          key: key,
          label: lc.cost_type_name,
          sortable: true,
          visible: true,
        }

        this.costTypeFields.push(column);

        total[key] = lc.sum;
      }.bind(this));

      items.forEach(function (item) {
        item.location_costs.forEach(function (lc) {
          let key = `location_cost_${lc.cost_type}`;
          item[key] = lc.sum;
        });
      });
    },
    // EChart
    toggleChart() {
      this.showChart = !this.showChart;

      if (this.showChart) {
        this.$nextTick(() => {
          this.$el.querySelector('.EChart').scrollIntoView({behavior: 'smooth', block: 'start'});
        });
      }
    },
    toggleIndexField(isAggregated) {
      const showIndex = !isAggregated;
      const indexExists = this.serviceStartField.some(f => f.key === 'index');

      if (showIndex && !indexExists) {
        this.serviceStartField.push({
          key: 'index',
          label: '№',
          class: 'text-center'
        });
      } else if (!showIndex && indexExists) {
        this.serviceStartField = this.serviceStartField.filter(f => f.key !== 'index');
      }
    },
    clearIndexField() {
      this.serviceStartField = [];
    },
    canReadOther() {
      return this.hasPermission([PERM.ANALYTIC_READ_OTHER]);
    },
  },
}
</script>

<style scoped>
#calculator-project td {
  padding-left: 10px;
  padding-right: 10px;
}
#header {
  z-index: 1040;
}

.page-table-container >>> .b-table-sticky-header > .table.b-table > tfoot > tr > th {
  position: sticky !important;
  bottom: 0 !important;
  background-color: #fff !important;
  z-index: 10 !important;
  box-shadow: inset 0 1px 0 #dee2e6;
}

.page-table-container >>> .b-table-sticky-header {
  overflow-anchor: none;
}
</style>



// WEBPACK FOOTER //
// src/components/Analytics/Analytics.vue